import React, {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { NavLink } from "react-router-dom";
import CompanyLogo from "../../assets/global/vis-group-final-PNG.png";
import {
  AnimatePresence,
  motion,
  useInView,
  useMotionValueEvent,
  useScroll,
} from "framer-motion";
//=============================Hero Icons===================================
import { XMarkIcon, Bars3Icon } from "@heroicons/react/24/solid";
import { navItems } from "../../utilities/navBarData";
import { FlayoutLink } from "./flayoutLink";
import DefaultPadding from "../../utilities/constants";
import NavBarAccordion from "./accordion";
import { servicesCategory } from "../../utilities/serviceData";
import useWindowSize from "../../hooks/windowSizehook";

const NavBar = () => {
  const [open, setOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [shadow, setShadow] = useState("");

  const { scrollY } = useScroll();

  const ref = useRef(null);
  const isInView = useInView(ref, { amount: 0.5, once: true });
  const [screenWidth] = useWindowSize();

  const staggeredAnimation = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.135 * index,
        duration: 0.5,
        type: "spring",
        stiffness: 250,
      },
    }),
  };

  const navHideVariants = {
    hidden: {
      y: "-100%",
    },
    visible: {
      y: 0,
    },
  };

  useMotionValueEvent(scrollY, "change", (value) => {
    const previousVal = scrollY.getPrevious();

    if (value > previousVal && value > 50) {
      setHidden(true);
    } else {
      setHidden(false);
    }

    if (value > 100) {
      setShadow("shadow-visShadow");
    } else {
      setShadow("");
    }
  });

  useEffect(() => {
    if (open) {
      document.body.style.overflowY = "hidden";
    } else {
      document.body.style.overflowY = "auto";
    }
  }, [open]);

  useLayoutEffect(() => {
    if (screenWidth >= 1024) {
      setOpen(false);
    }
  }, [screenWidth]);

  const toggleMenu = () => {
    setOpen((prev) => !prev);
  };

  const padding = new DefaultPadding();
  const defaultPaddingX = padding.getDefaultPaddingX();
  const defaultPaddingY = padding.getDefaultPaddingY();

  return (
    <>
      <motion.nav
        variants={navHideVariants}
        animate={hidden ? "hidden" : "visible"}
        transition={{ duration: 0.35, ease: "easeInOut" }}
        ref={ref}
        className={`fixed top-0 z-20 flex h-[95.0px] w-full items-center justify-between bg-white font-Poppins transition-shadow duration-500 ${shadow} ${defaultPaddingX}`}
      >
        <div className="w-40 max-w-full pr-1">
          <NavLink to="/#" className="block w-full py-3">
            <img className="scale-125" src={CompanyLogo} alt="logo" />
          </NavLink>
        </div>
        <div ref={ref} className="hidden gap-10 lg:flex">
          {navItems.map((item, index) => {
            return (
              <motion.div
                key={index + item.label}
                variants={staggeredAnimation}
                initial={"hidden"}
                animate={isInView ? "visible" : "hidden"}
                custom={index}
                className="last:hidden"
              >
                {Object.hasOwn(item, "flayoutContent") ? (
                  <FlayoutLink
                    key={item.label}
                    href={item.route}
                    FlayoutContent={item.flayoutContent}
                  >
                    {item.label}
                  </FlayoutLink>
                ) : (
                  <NavLink
                    key={item.label}
                    className={({ isActive }) =>
                      `text-base text-visBlue hover:text-visOrange ${isActive && "text-visOrange"}`
                    }
                    to={item.route}
                  >
                    {item.label}
                  </NavLink>
                )}
              </motion.div>
            );
          })}
        </div>
        <button
          onClick={toggleMenu}
          id="navbarToggler"
          className="relative flex h-10 w-10 items-center justify-center rounded-md bg-visBlue px-2 py-1 lg:hidden"
        >
          <Bars3Icon className="size-full text-white" />
        </button>
      </motion.nav>
      {/* mobile View */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: "100vw" }}
            animate={{ x: 0 }}
            exit={{ x: "100vw" }}
            transition={{ duration: 0.4 }}
            className="fixed left-0 top-0 z-20 flex h-[100vh] w-full flex-col justify-center overflow-y-auto overflow-x-hidden bg-visLightBlue py-5"
          >
            <div
              className={`absolute right-0 top-0 z-20 flex w-fit items-center justify-end ${defaultPaddingX} h-[95.2px]`}
            >
              <button className="h-10 w-10" onClick={toggleMenu}>
                <XMarkIcon className="text-visBlue" />
              </button>
            </div>

            <div
              className={`relative ${defaultPaddingX} w-fit space-y-3 md:space-y-5`}
            >
              <div className="w-fit space-y-3 md:space-y-5">
                {navItems.map((item, index) => {
                  return (
                    <Fragment key={index}>
                      {!Object.hasOwn(item, "flayoutContent") && (
                        <div>
                          <NavLink
                            key={index}
                            onClick={toggleMenu}
                            to={item.route}
                            className="text-start text-base font-normal text-visBlue hover:text-visOrange md:text-xl"
                          >
                            {item.label}
                          </NavLink>
                        </div>
                      )}
                    </Fragment>
                  );
                })}
              </div>
              <div>
                {navItems.map((item, index) => {
                  return (
                    <Fragment key={index}>
                      {Object.hasOwn(item, "flayoutContent") && (
                        <>
                          <NavLink
                            key={index}
                            onClick={toggleMenu}
                            to={item.route}
                            className={`text-start text-base font-normal text-visBlue hover:text-visOrange md:text-xl`}
                          >
                            {item.label}
                          </NavLink>
                          <div className="flex flex-wrap gap-x-10">
                            {servicesCategory.map((item, idx) => {
                              return (
                                <NavBarAccordion
                                  key={idx}
                                  toggleMenu={toggleMenu}
                                  title={item.name}
                                  items={item.child}
                                  open={isOpen === idx}
                                  onClick={() =>
                                    setIsOpen((prev) =>
                                      isOpen === idx ? null : idx,
                                    )
                                  }
                                />
                              );
                            })}
                          </div>
                        </>
                      )}
                    </Fragment>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default NavBar;
